import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type HealthResponse, type ReadyResponse } from '@apm/contracts';
import { RagBadge } from '../../components/ui/rag-badge';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/states';
import { BackgroundJobsPanel } from '../../features/system-metrics/components/background-jobs-panel';
import { SystemMetricsPanel } from '../../features/system-metrics/components/system-metrics-panel';

export const Route = createFileRoute('/_app/system')({ component: SystemPage });

/**
 * Trang trạng thái: chuỗi web → api → mysql.
 *
 * Probe nằm ở gốc (`/healthz`, `/readyz`), ngoài prefix `/api/v1` và không cần
 * xác thực — k8s gọi thẳng chúng. Nên ở đây dùng `fetch` trần chứ không phải
 * `apiFetch`.
 */
function SystemPage(): ReactNode {
  const { t } = useTranslation();

  const probes = useQuery({
    queryKey: ['probes'],
    queryFn: async () => {
      const [health, ready] = await Promise.all([
        fetch('/healthz').then((r) => r.json() as Promise<HealthResponse>),
        fetch('/readyz').then((r) => r.json() as Promise<ReadyResponse>),
      ]);
      return { health, ready };
    },
    refetchInterval: 15_000,
    staleTime: 0,
  });

  const dbUp = probes.data?.ready.checks.database === 'up';
  const apiUp = probes.data?.health.status === 'ok';

  return (
    <div className="@container flex flex-col gap-4">
      <h1 className="text-2xl font-semibold tracking-tight">{t('system.title')}</h1>

      {/*
        Số liệu đứng **trên** chuỗi probe — APM-SA-a.

        Probe trả lời "hệ thống còn sống không", một câu chỉ đáng đọc khi đang
        nghi có sự cố. Câu hằng ngày của người vận hành là "hệ thống đang được
        dùng tới đâu", nên nó ở trên.
      */}
      <SystemMetricsPanel />

      {/*
        Luồng ngầm — APM-SYSMON-d. Dưới số liệu, trên chuỗi probe: "job nền có
        chạy đúng không" là câu hỏi hằng ngày hơn "API còn sống không".
      */}
      <BackgroundJobsPanel />

      <h2 className="text-sm font-medium">{t('system.probesTitle')}</h2>

      <Card>
        <CardContent className="flex flex-col gap-1 p-2">
          <Row label={t('system.web')} status="green" detail={t('system.running')} />

          {probes.isPending ? (
            <>
              <Skeleton className="mx-4 my-3 h-5" />
              <Skeleton className="mx-4 my-3 h-5" />
            </>
          ) : (
            <>
              <Row
                label={t('system.api')}
                status={apiUp ? 'green' : 'red'}
                detail={
                  probes.data
                    ? `v${probes.data.health.version} · ${probes.data.health.uptimeSec}s`
                    : t('errors.network')
                }
              />
              <Row
                label={t('system.database')}
                status={dbUp ? 'green' : 'red'}
                detail={dbUp ? t('system.connected') : t('system.disconnected')}
              />
            </>
          )}
        </CardContent>
      </Card>

      {probes.data && !dbUp && (
        <p className="text-sm text-muted-foreground">
          {t('system.mysqlHint')}{' '}
          <code className="rounded bg-muted px-1">docker compose up -d mysql</code>
        </p>
      )}
      {probes.isError && <p className="text-sm text-muted-foreground">{t('system.apiHint')}</p>}
    </div>
  );
}

function Row({
  label,
  status,
  detail,
}: {
  label: string;
  status: 'green' | 'red';
  detail: string;
}): ReactNode {
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
      {/* Badge kèm icon + nhãn, không chỉ chấm màu — người mù màu vẫn đọc được. */}
      <RagBadge status={status} label={label} className="min-w-44 justify-start" />
      <span className="text-sm text-muted-foreground">{detail}</span>
    </div>
  );
}
